/**
## Relative Date Formatter
@author: jhk

#### Format date or time relative to present moment

less than 10s in the past or future return 'now'
10s - 59s return 'in X seconds' or 'X seconds ago'
60s - 59m:59s return 'in X minute(s)' or 'X minute(s) ago'
1hr - midnight returns 'in X hour(s)' or 'X hour(s) ago'
12am - 11:59:59pm tomorrow is 'tomorrow'
^ +1 day is 'in two days'
^ +2 day is 'in three days'
11:59:59pm - 12am yesterday is 'yesterday'
^ -1 day is '2 days ago'
^ -2 days is '3 days ago'

Dates more than three days into the past or future
but still within the current year are formatted "May 5"
Non-current year will format as "May 5, 2012"
*/
import moment from 'moment';
const MILLISECONDS_PER_SECOND = 1000;
const ONE_MINUTE_IN_SECONDS = 60;
const ONE_HOUR_IN_SECONDS = 60 * 60;
const ONE_DAY_IN_SECONDS = 24 * 60 * 60;
const RESOLUTION = {
  s: 10,
  // these refer to the span of time considered 'just now'
  m: ONE_MINUTE_IN_SECONDS,
  h: ONE_HOUR_IN_SECONDS,
  d: ONE_DAY_IN_SECONDS
};
function secondsToHours(seconds) {
  return Math.floor(seconds / ONE_HOUR_IN_SECONDS);
}
function secondsToMinutes(seconds) {
  return Math.floor(seconds / ONE_MINUTE_IN_SECONDS);
}
function formatCurrentDayPast(i18n, seconds) {
  if (seconds < ONE_MINUTE_IN_SECONDS) {
    return i18n.t('invoices.time.past.seconds', {
      count: seconds
    });
  }
  if (seconds < ONE_HOUR_IN_SECONDS) {
    return i18n.t('invoices.time.past.minutes', {
      count: secondsToMinutes(seconds)
    });
  }
  return i18n.t('invoices.time.past.hours', {
    count: secondsToHours(seconds)
  });
}
function formatCurrentDayFuture(i18n, seconds) {
  if (seconds < ONE_MINUTE_IN_SECONDS) {
    return i18n.t('invoices.time.future.seconds', {
      count: seconds
    });
  }
  if (seconds < ONE_HOUR_IN_SECONDS) {
    return i18n.t('invoices.time.future.minutes', {
      count: secondsToMinutes(seconds)
    });
  }
  return i18n.t('invoices.time.future.hours', {
    count: secondsToHours(seconds)
  });
}
function isBeforeEndOfDay(seconds, base, day_offset = 0) {
  const eod = moment(base).add(day_offset, 'd').endOf('day');
  return moment(base).add(seconds, 's').isBefore(eod);
}
function isAfterStartOfDay(seconds, base, day_offset = 0) {
  const sod = moment(base).subtract(day_offset, 'd').startOf('day');
  return moment(base).subtract(seconds, 's').isAfter(sod);
}

/**
 * Day boundary function callback
 *
 * @callback dayBoundaryFn
 * @param {number} seconds - time diff in seconds
 * @param {number} day_offset=0 - the number of days into the past or future to chec
 */

/**
 * @param {number} seconds - the time diff in seconds
 * @param {number} base - moment() or a passed in comparison time
 * @param {dayBoundaryFn} dayBoundaryFn - the day boundary function used to determine how
 * many days into the past or future this date is.
 *
 * @returns {string} the relative date string
 */
function countDays(seconds, base, dayBoundaryFn) {
  let days = 0;

  // terminate the first time seconds
  // is within the day boundary
  // e.g. before midnight on a day in the future
  // or after midnight on a day in the past
  while (!dayBoundaryFn(seconds, base, days)) {
    days++;
  }
  return days;
}
function formatFuture(i18n, seconds, base) {
  if (isBeforeEndOfDay(seconds, base)) {
    return formatCurrentDayFuture(i18n, seconds);
  }
  return i18n.t('invoices.time.future.days', {
    count: countDays(seconds, base, isBeforeEndOfDay)
  });
}
function formatPast(i18n, seconds, base) {
  if (isAfterStartOfDay(seconds, base)) {
    return formatCurrentDayPast(i18n, seconds);
  }
  return i18n.t('invoices.time.past.days', {
    count: countDays(seconds, base, isAfterStartOfDay)
  });
}
function validateInput(datetime) {
  if (moment.isMoment(datetime) ||
  // moment()
  datetime instanceof Date ||
  // new Date()
  typeof datetime === 'number') {
    // Date.now()
    return;
  }
  throw new Error('invalid DateTime input, expects a Date, Moment, or timestamp in ms');
}
export default class RelativeDateFormatter {
  /**
   * @constructor
   * @param {service} [i18n] - i18n service
   * @param {service} [localeTime] - localeTime service
   * @param {string=} [resolution='s'] - refers to the minimum unit of relative time
   *  's' - 10s into the past or future returns 'just now'
   *  'm' - 59s into the past or future returns 'just now'
   *  'h' - 59m:59s into the past or future returns 'just now'
   *  'd' - 12am - 11:59:59pm returns 'today'
   *
   * @param {number=} [futureDaysOffset=3] - non-negative integer
   *  the number of days in the future that
   *  will return 'in X days'
   *
   * @param {number=} [pastDaysOffset=3] - the same for the past
   *  0 is today, 1 is tomorrow or yesterday, 2 is 'in 2 days' or '2 days ago'
   */
  constructor(i18n, localeTime, {
    resolution = 's',
    futureDaysOffset = 3,
    pastDaysOffset = 3
  } = {}) {
    if (!['s', 'm', 'h', 'd'].includes(resolution)) {
      throw new Error("resolution must be one of 's', 'm', 'h', or 'd'");
    }
    if (futureDaysOffset < 0) {
      throw new Error('futureDaysOffset must be a non-negative integer');
    }
    if (pastDaysOffset < 0) {
      throw new Error('pastDaysOffset must be a non-negative integer');
    }
    this.i18n = i18n;
    this.localeTime = localeTime;
    this.options = {
      resolution,
      futureDaysOffset,
      pastDaysOffset
    };
  }

  /**
   * @param {number} datetime - a new Date(), moment(), or timestamp in milliseconds
   * @param {number=} [basetime=moment()] - now, or the time passed in
   * @returns {string} the formatted relative date string
   */
  format(datetime, basetime = moment()) {
    validateInput(datetime);
    validateInput(basetime);
    const diffSeconds = Math.floor((datetime - basetime) / MILLISECONDS_PER_SECOND);
    const absoluteSeconds = Math.abs(diffSeconds);

    // Determine the lower bound
    // Resolution determines times considered 'just now' or 'today'
    if (absoluteSeconds < RESOLUTION[this.options.resolution]) {
      if (this.options.resolution === 'd') {
        return this.i18n.t('invoices.time.today');
      }
      return this.i18n.t('invoices.time.now');
    }

    // handle the upper bound
    // returns exact dates for times outside the day offsets
    if (!isBeforeEndOfDay(absoluteSeconds, basetime, this.options.futureDaysOffset) || !isAfterStartOfDay(absoluteSeconds, basetime, this.options.pastDaysOffset)) {
      if (moment(datetime).isSame(moment(basetime), 'year')) {
        // this year
        return this.localeTime.dateTime(moment(datetime), 'longMonthDay');
      }
      return this.localeTime.dateTime(moment(datetime), 'longCardinalDate');
    }
    if (diffSeconds > 0) {
      return formatFuture(this.i18n, absoluteSeconds, basetime);
    } else {
      return formatPast(this.i18n, absoluteSeconds, basetime);
    }
  }
}